import axios from 'axios';

// URL base para las peticiones a la API.
export const SERVER_URL = 'https://overlord.chat';
//export const SERVER_URL = 'http://192.168.1.58';

const BASE_URL = SERVER_URL + '/api/v1/back/db';

const ERROR_USER_DISCONNECTED = 33;
const ERROR_SESSION_NOTFOUND = 40;

const getToken = () => {
  const user = JSON.parse(sessionStorage.getItem('user'));
  return user?.token;
};

const axiosInstance = axios.create({
  timeout: 30000, // 30 segundos
  headers: {
    'Content-Type': 'application/json',
  }
});

const retryRequest = async (fn, retries = 3, delay = 1000) => {
  try {
    return await fn();
  } catch (error) {
    if (retries === 0) throw error;
    await new Promise(resolve => setTimeout(resolve, delay));
    return retryRequest(fn, retries - 1, delay * 2);
  }
};



/**
 * Maneja los errores de la API de forma centralizada
 * @param {Error} error - Error capturado
 * @returns {never} - Nunca retorna, siempre lanza un error
 */
const manejarError = (error) => {

  if (error.response) {
    // Si el error viene del servido
    
    const code = error.response.data?.error?.code;
    const message = error.response.data?.error?.message;
    
    // Si es error de desconexión o sesión no encontrada
    if (code === ERROR_USER_DISCONNECTED || code === ERROR_SESSION_NOTFOUND) {
      // Limpiar la sesión
      sessionStorage.clear();
      // Redirigir al login
      window.location.href = '/login';
    }

    console.error('❌ Error del servidor:', {
      código: code,
      mensaje: message
    });
    throw new Error(message || 'Error desconocido');
  } else if (error.request) {
    // Si la petición nunca recibió respuesta
    console.error('❌ Error en llamada a API: No se recibió respuesta del servidor');
    throw new Error('No se pudo conectar al servidor. Verifica tu conexión.');
  } else {
    // Otros errores (errores de red, timeout, etc)
    console.error('❌ Error en la petición:', error.message);
    throw new Error(error.message || 'Error desconocido');
  }
};

/**
 * Obtiene los datos generales para el dashboard.
 * @param {number} tipo - Tipo de consulta.
 * @param {string} chatbot - Nombre del chatbot.
 * @param {string} fecha1 - Fecha de inicio (formato YYYYMMDD).
 * @param {string} fecha2 - Fecha de fin (formato YYYYMMDD).
 * @returns {Promise<object>} - Respuesta del backend.
 */
export const fetchData = async (tipo, chatbot, fecha1, fecha2) => {
  try {
    const sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      throw new Error('No hay sesión activa');
    }

    const response = await axiosInstance.get(BASE_URL, {
      params: {
        tipo,
        chatbot,
        fecha1,
        fecha2,
      },
      headers: {
        'Authorization': `Bearer ${sessionId}`
      }
    });
    return response.data;
  } catch (error) {
    manejarError(error);
  }
};

/**
 * Obtiene los datos de una conversación específica.
 * @param {number} tipo - Tipo de consulta.
 * @param {string} chatbot - Nombre del chatbot.
 * @param {string} fecha1 - Fecha de inicio (formato YYYYMMDD).
 * @param {string} fecha2 - Fecha de fin (formato YYYYMMDD).
 * @param {string} chatid - ID del chat específico.
 * @returns {Promise<object>} - Respuesta del backend con la conversación.
 */
export const fetchChatConversation = async (tipo, chatbot, fecha1, fecha2, chatid) => {
  try {
    const sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      throw new Error('No hay sesión activa');
    }

    const response = await axiosInstance.get(BASE_URL, {
      params: {
        tipo,
        chatbot,
        fecha1,
        fecha2,
        vertexto: 1,
        chatid,
      },
      headers: {
        'Authorization': `Bearer ${sessionId}`
      }
    });
    return response.data;
  } catch (error) {
    manejarError(error);
  }
};

/**
 * Obtiene los datos de texto del chatbot.
 * @param {string} chatbotId - ID del chatbot.
 * @returns {Promise<object>} - Respuesta del backend con los datos de texto.
 */
export const fetchTextData = async (chatbotId) => {
  try {
    const sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      throw new Error('No hay sesión activa');
    }

    const response = await axiosInstance.get(SERVER_URL + '/api/v1/back/text-data', {
      params: {
        id: chatbotId
      },
      headers: {
        'Authorization': `Bearer ${sessionId}`
      }
    });

    return response.data;
  } catch (error) {
    manejarError(error);
  }
};

/**
 * Envía un mensaje al chatbot y obtiene su respuesta
 * @param {string} chatbotId - ID del chatbot
 * @param {string} message - Mensaje del usuario
 * @param {string} chatId - ID de la conversación (opcional)
 * @returns {Promise<object>} - Respuesta del chatbot
 */
export const sendChatMessage = async (chatbotId, message, chatId = '') => {
  try {
    const sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      throw new Error('No hay sesión activa');
    }

    const API_URL = SERVER_URL + "/api/v1/chat";

    const response = await axios.post(API_URL, {
      clientCode: chatbotId,
      chatId: chatId || '', // Asegurarse de enviar vacío en lugar de null
      message: message,
    }, {
      timeout: 60000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${sessionId}`
      },
      validateStatus: function (status) {
        return status >= 200 && status < 500;
      }
    });

    if (!response.data) {
      throw new Error('Respuesta vacía del servidor');
    }

    return response.data;
  } catch (error) {
    manejarError(error);
  }
};

/**
 * Guarda las instrucciones del chatbot
 * @param {string} chatbotId - Código del cliente
 * @param {string} instructions - Texto de las instrucciones
 * @returns {Promise<void>} - Promesa vacía que se resuelve cuando se guarda correctamente
 */
export const saveInstructions = async (chatbotId, instructions) => {
  try {
    const sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      throw new Error('No hay sesión activa');
    }

    const API_URL = SERVER_URL + "/api/v1/back/save-data";

      const response = await fetch(`${SERVER_URL}/api/v1/back/usr?ope=prd_savedata&prd=${chatbotId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain',
        'Authorization': `Bearer ${sessionId}`
      },
      body: instructions
    });

    if (!response.ok) {
      throw new Error('Error al guardar las instrucciones');
    }

    // No intentamos parsear la respuesta como JSON
    return;
  } catch (error) {
    manejarError(error);
  }
};

/**
 * Obtiene los datos del chatbot
 * @param {string} chatbotId - ID del chatbot
 * @returns {Promise<object>} - Respuesta del backend con los datos del chatbot
 */
export const fetchChatbotData = async (chatbotId) => {
  try {
    const sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      throw new Error('No hay sesión activa');
    }

    const response = await fetch(SERVER_URL + `/api/v1/back/chatbot-data?id=${chatbotId}`, {
      headers: {
        'Authorization': `Bearer ${sessionId}`
      }
    });
    const data = await response.json();
    return data;
  } catch (error) {
    manejarError(error);
  }
};

/**
 * Solicita la recuperación de contraseña para un correo electrónico
 * @param {string} email - Correo electrónico del usuario
 * @returns {Promise<object>} - Respuesta del backend con el keylink
 */
export const requestPasswordRecovery = async (email) => {
  try {
    const response = await axiosInstance.post(`${SERVER_URL}/api/v1/back/usr?ope=user_passforget`, {
      email: email,
    });
    return response.data;
  } catch (error) {
    manejarError(error);
  }
};

/**
 * Resetea la contraseña de un usuario usando el linkcode
 * @param {string} linkcode - Código único para resetear la contraseña
 * @param {string} newPassword - Nueva contraseña hasheada del usuario
 * @returns {Promise<object>} - Respuesta del backend
 */
export const resetPassword = async (linkcode, newPassword) => {
  try {
    const response = await axiosInstance.post(`${SERVER_URL}/api/v1/back/usr?ope=user_passnew`, {
      linkcode: linkcode,
      passnew: newPassword
    });

    return response.data;
  } catch (error) {
    manejarError(error);
  }
};

/**
 * Realiza el login del usuario.
 * @param {string} email - Correo electrónico del usuario.
 * @param {string} password - Contraseña sin hashear del usuario.
 * @returns {Promise<object>} - Datos del usuario y token de sesión.
 */
export const login = async (email, password) => {
  try {
    const response = await axiosInstance.post(`${SERVER_URL}/api/v1/back/usr?ope=user_login`, {
      email: email,
      passhash: password
    });

    if (response.data) {
      const { session, ...userData } = response.data;
      
      // Aseguramos que la foto tenga el prefijo correcto si existe
      if (userData.photo) {
        userData.photo = `/photos/${userData.photo}`;
      }

      // Creamos el objeto final con todos los datos actualizados
      const updatedUserData = { ...userData, session };
      
      sessionStorage.setItem('sessionId', session);
      sessionStorage.setItem('user', JSON.stringify(updatedUserData));
      console.log(updatedUserData);
      return updatedUserData;
    }

    return null;
  } catch (error) {
    manejarError(error);
  }
};

/**
 * Cierra la sesión del usuario.
 * @returns {Promise<void>} - Promesa vacía que se resuelve cuando se cierra la sesión correctamente.
 */
export const logout = async () => {
  try {
    const sessionId = sessionStorage.getItem('sessionId');

    // Si hay sessionId, intentamos hacer logout en el backend
    if (sessionId) {
      try {
        await axiosInstance.post(`${SERVER_URL}/api/v1/back/usr?ope=user_logout`, null, {
          headers: {
            'Authorization': `Bearer ${sessionId}`
          }
        });
      } catch (error) {
        console.error('Error al cerrar sesión en el backend:', error);
        // Continuamos con la limpieza local aunque falle el backend
      }
    }

    // Siempre limpiamos el sessionStorage
    sessionStorage.removeItem('sessionId');
    sessionStorage.removeItem('user');

    return { success: true };
  } catch (error) {
    manejarError(error);
  }
};

/**
 * Edita el nombre del usuario.
 * @param {string} newName - Nuevo nombre del usuario
 * @returns {Promise<object>} - Datos actualizados del usuario
 */
export const editUserName = async (newName) => {
  try {
    const sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      throw new Error('No hay sesión activa');
    }

    const response = await axiosInstance.post(`${SERVER_URL}/api/v1/back/usr?ope=user_edit`, {
      name: newName
    }, {
      headers: {
        'Authorization': `Bearer ${sessionId}`
      }
    });

    if (response.data) {
      // Actualizar los datos del usuario en sessionStorage
      const userData = JSON.parse(sessionStorage.getItem('user'));
      const updatedUserData = {
        ...userData,
        name: newName
      };
      sessionStorage.setItem('user', JSON.stringify(updatedUserData));
      return updatedUserData;
    }

    return response.data;
  } catch (error) {
    manejarError(error);
  }
};

/**
 * Cambia la contraseña del usuario.
 * @param {string} oldPassword - Contraseña actual
 * @param {string} newPassword - Nueva contraseña
 * @returns {Promise<object>} - Respuesta del servidor
 */
export const changePassword = async (oldPassword, newPassword) => {
  try {
    const sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      throw new Error('No hay sesión activa');
    }

    const response = await axiosInstance.post(`${SERVER_URL}/api/v1/back/usr?ope=user_passchange`, {
      passold: oldPassword,
      passnew: newPassword
    }, {
      headers: {
        'Authorization': `Bearer ${sessionId}`
      }
    });

    return response.data;
  } catch (error) {
    manejarError(error);
  }
};

/**
 * Crea una nueva organización y actualiza la lista de organizaciones en sessionStorage
 * @param {string} name - Nombre de la organización
 * @param {string} parentorg - Código de la organización padre
 * @returns {Promise<object>} - Respuesta del servidor
 */
export const createOrganization = async (name, parentorg) => {
  try {
    const sessionId = sessionStorage.getItem('sessionId');

    if (!sessionId) {
      throw new Error('No hay sesión activa');
    }

    const response = await axiosInstance.post(`${SERVER_URL}/api/v1/back/usr?ope=org_new`, {
      name: name,
      parentorg: parentorg,
      active: true
    }, {
      headers: {
        'Authorization': `Bearer ${sessionId}`
      }
    });

    // Si la creación fue exitosa, actualizamos todos los datos del usuario en sessionStorage
    if (response.data) {
      const userData = JSON.parse(sessionStorage.getItem('user'));
      const updatedUserData = {
        ...userData,
        email: response.data.email || userData.email,
        name: response.data.name || userData.name,
        organization: response.data.organization || userData.organization,
        organizations: response.data.organizations || userData.organizations,
        products: response.data.products || userData.products,
        rol: response.data.rol || userData.rol
      };
      sessionStorage.setItem('user', JSON.stringify(updatedUserData));
    }

    return response.data;
  } catch (error) {
    manejarError(error);
  }
};

/**
 * Obtiene la lista de usuarios
 * @returns {Promise<object>} - Lista de usuarios
 */
export const getUsersList = async () => {
  try {
    const sessionId = sessionStorage.getItem('sessionId');

    if (!sessionId) {
      throw new Error('No hay sesión activa');
    }

    const response = await axiosInstance.post(`${SERVER_URL}/api/v1/back/usr?ope=users_list`, {}, {
      headers: {
        'Authorization': `Bearer ${sessionId}`
      }
    });

    return response.data;
  } catch (error) {
    manejarError(error);
  }
};

/**
 * Crea un nuevo usuario
 * @param {string} organization - Código de la organización
 * @param {string} email - Email del usuario
 * @param {string} name - Nombre del usuario
 * @param {string} rol - Rol del usuario
 * @param {string} passhash - Contraseña hasheada
 * @param {string} passvisible - Contraseña visible generada
 * @returns {Promise<object>} - Respuesta del servidor
 */
export const user_new = async (organization, email, name, rol, passhash, passvisible) => {
  try {
    const sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      throw new Error('No hay sesión activa');
    }
    const response = await axiosInstance.post(`${SERVER_URL}/api/v1/back/usr?ope=user_new`, {
      organization,
      email,
      name,
      rol,
      passhash,
      passvisible
    }, {
      headers: {
        'Authorization': `Bearer ${sessionId}`
      }
    });

    return response.data;
  } catch (error) {
    manejarError(error);
  }
};

/**
 * Obtiene la configuración del chatbot
 * @param {string} chatbotId - ID del chatbot
 * @returns {Promise<object>} - Configuración del chatbot
 */
export const getChatbotConfig = async (chatbotId) => {
  try {
    const sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      throw new Error('No hay sesión activa');
    }

    const response = await axiosInstance.get(`${SERVER_URL}/api/v1/back/chatbot-data`, {
      params: { id: chatbotId },
      headers: {
        'Authorization': `Bearer ${sessionId}`
      }
    });
    return response.data;
  } catch (error) {
    manejarError(error);
  }
};

/**
 * Realiza la suscripción pública (sin sesión)
 * @param {string} useremail - Email del usuario
 * @param {string} username - Nombre del usuario
 * @param {string} prdname - Nombre del asistente/web
 * @param {string} passhash - Contraseña hasheada
 * @param {string} passvisible - Contraseña visible generada
 * @param {string} linkcode - Código de invitación
 * @returns {Promise<object>} - Respuesta del servidor
 */
export const subs_completed = async (useremail, username, prdname, passhash, passvisible, linkcode) => {
  try {
    const response = await axios.post(
      `${SERVER_URL}/api/v1/back/usr?ope=subs_completed`,
      {
        useremail,
        username,
        prdname,
        passhash,
        passvisible,
        linkcode
      },
      {
        headers: { 'Content-Type': 'application/json' }
      }
    );
    return response.data;
  } catch (error) {
    manejarError(error);
  }
};

/**
 * Guarda la configuración del chatbot
 * @param {string} chatbotId - ID del chatbot
 * @param {object} config - Nueva configuración del chatbot
 * @returns {Promise<void>} - Promesa vacía que se resuelve cuando se guarda correctamente
 */
export const saveChatbotConfig = async (chatbotId, config) => {
  try {
    const sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      throw new Error('No hay sesión activa');
    }

    var jsonText = JSON.stringify(config,null,2);
    const response = await fetch(`${SERVER_URL}/api/v1/back/usr?ope=prd_saveconfig&prd=${chatbotId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain',
        'Authorization': `Bearer ${sessionId}`
      },
      body: jsonText
    });

    if (!response.ok) {
      throw new Error('Error al guardar la configuración');
    }

    // No intentamos parsear la respuesta como JSON
    return;
  } catch (error) {
    manejarError(error);
  }
};

/**
 * Guarda las opciones del chatbot
 * @param {string} chatbotId - ID del chatbot
 * @param {string} optionsStr - Cadena de opciones del chatbot
 * @returns {Promise<void>} - Promesa vacía que se resuelve cuando se guarda correctamente
 */
export const saveChatbotOptions = async (chatbotId, optionsStr) => {
  try {
    const sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      throw new Error('No hay sesión activa');
    }

    const response = await fetch(`${SERVER_URL}/api/v1/back/usr?ope=prd_saveoptions&prd=${chatbotId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain',
        'Authorization': `Bearer ${sessionId}`
      },
      body: optionsStr
    });

    if (!response.ok) {
      throw new Error('Error al guardar las opciones');
    }

    // No intentamos parsear la respuesta como JSON
    return;
  } catch (error) {
    manejarError(error);
  }
};

/**
 * Login como otro usuario (solo para admins)
 * @param {string} userEmail - Email del usuario al que queremos acceder
 * @returns {Promise<object>} - Datos del usuario y token de sesión
 */
export const loginAsUser = async (userEmail) => {
  try {
    const sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      throw new Error('No hay sesión activa');
    }

    const response = await axiosInstance.post(`${SERVER_URL}/api/v1/back/usr?ope=user_loginadmin`, {
      email: userEmail
    }, {
      headers: {
        'Authorization': `Bearer ${sessionId}`
      }
    });

    if (response.data) {
      return response.data;
    }

    throw new Error('Error al hacer login como usuario');
  } catch (error) {
    manejarError(error);
  }
};

/**
 * Obtiene el hash para generar los enlaces de las demos
 * @param {string} chatbotId - ID del chatbot
 * @returns {Promise<object>} - Objeto con el hash para las demos
 */
export const getDemoData = async (chatbotId) => {
  try {
    const sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      throw new Error('No hay sesión activa');
    }

    const response = await axiosInstance.post(`${SERVER_URL}/api/v1/back/usr?ope=prd_demodata&prd=${chatbotId}`, {}, {
      headers: {
        'Authorization': `Bearer ${sessionId}`
      }
    });

    return response.data;
  } catch (error) {
    manejarError(error);
  }
};

/**
 * Elimina un usuario del sistema
 * @param {string} email - Email del usuario a eliminar
 * @returns {Promise<object>} - Respuesta del servidor
 */
export const deleteUser = async (email) => {
  try {
    const sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      throw new Error('No hay sesión activa');
    }

    const response = await axiosInstance.post(`${SERVER_URL}/api/v1/back/usr?ope=user_del`, {
      email: email
    }, {
      headers: {
        'Authorization': `Bearer ${sessionId}`
      }
    });
    return response.data;
  } catch (error) {
    manejarError(error);
  }
};

/**
 * Modifica los datos de un usuario
 * @param {string} currentEmail - Email actual del usuario
 * @param {object} userData - Datos actualizados del usuario
 * @returns {Promise<object>} - Respuesta del servidor
 */
export const modifyUser = async (currentEmail, userData) => {
  try {
    const sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      throw new Error('No hay sesión activa');
    }

    const response = await axiosInstance.post(`${SERVER_URL}/api/v1/back/usr?ope=user_modify`, {
      email: currentEmail,
      emailnew: userData.email,
      name: userData.name,
      rol: userData.rol,
      organization: userData.organization
    }, {
      headers: {
        'Authorization': `Bearer ${sessionId}`
      }
    });
    return response.data;
  } catch (error) {
    manejarError(error);
  }
};

/**
 * Elimina una organización
 * @param {string} code - Código de la organización
 * @returns {Promise<object>} - Respuesta del servidor con los datos actualizados del usuario
 */
export const deleteOrganization = async (code) => {
  try {
    const sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      throw new Error('No hay sesión activa');
    }

    const response = await axiosInstance.post(`${SERVER_URL}/api/v1/back/usr?ope=org_del`, {
      code: code
    }, {
      headers: {
        'Authorization': `Bearer ${sessionId}`
      }
    });

    // Actualizar datos del usuario en sesión
    if (response.data) {
      const userData = JSON.parse(sessionStorage.getItem('user'));
      const updatedUserData = {
        ...userData,
        email: response.data.email || userData.email,
        name: response.data.name || userData.name,
        organization: response.data.organization || userData.organization,
        organizations: response.data.organizations || userData.organizations,
        products: response.data.products || userData.products,
        rol: response.data.rol || userData.rol
      };
      sessionStorage.setItem('user', JSON.stringify(updatedUserData));
    }

    return response.data;
  } catch (error) {
    manejarError(error);
  }
};

/**
 * Modifica una organización
 * @param {string} code - Código de la organización
 * @param {string} name - Nuevo nombre de la organización
 * @returns {Promise<object>} - Respuesta del servidor con los datos actualizados del usuario
 */
export const modifyOrganization = async (code, name) => {
  try {
    const sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      throw new Error('No hay sesión activa');
    }

    const response = await axiosInstance.post(`${SERVER_URL}/api/v1/back/usr?ope=org_modify`, {
      code: code,
      name: name
    }, {
      headers: {
        'Authorization': `Bearer ${sessionId}`
      }
    });

    // Actualizar datos del usuario en sesión
    if (response.data) {
      const userData = JSON.parse(sessionStorage.getItem('user'));
      const updatedUserData = {
        ...userData,
        email: response.data.email || userData.email,
        name: response.data.name || userData.name,
        organization: response.data.organization || userData.organization,
        organizations: response.data.organizations || userData.organizations,
        products: response.data.products || userData.products,
        rol: response.data.rol || userData.rol
      };
      sessionStorage.setItem('user', JSON.stringify(updatedUserData));
    }

    return response.data;
  } catch (error) {
    manejarError(error);
  }
};

/**
 * Sube una imagen de avatar para el usuario
 * @param {File} image - Archivo de imagen a subir
 * @returns {Promise<object>} - Datos actualizados del usuario
 */
export const uploadUserAvatar = async (image) => {
  try {
    const formData = new FormData();
    formData.append('image', image);

    const sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      throw new Error('No hay sesión activa');
    }

    const response = await axiosInstance.post(`${SERVER_URL}/api/v1/back/usr?ope=user_photo`, 
      formData,
      {
        headers: {
          'Authorization': `Bearer ${sessionId}`,
          'Content-Type': 'multipart/form-data'
        }
      }
    );

    if (response.data) {
      // Actualizamos los datos del usuario en sessionStorage
      const userData = response.data;
      // Aseguramos que la foto tenga el prefijo correcto
      if (userData.photo) {
        userData.photo = `/photos/${userData.photo}`;
      }
      sessionStorage.setItem('user', JSON.stringify(userData));
      return userData;
    }
    return response.data;
  } catch (error) {
    return manejarError(error);
  }
};

/**
 * Obtiene las impresiones del asistente para un rango de fechas
 * @param {string} prd - Código del asistente
 * @param {string} date1 - Fecha inicial (formato YYYYMMDD)
 * @param {string} date2 - Fecha final (formato YYYYMMDD)
 * @returns {Promise<Array>} - Lista de impresiones por día
 */
export const getImpressions = async (prd, date1, date2) => {
    try {
    const sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      throw new Error('No hay sesión activa');
    }
    // Convertir date1 y date2 de string a int
    date1 = parseInt(date1, 10);
    date2 = parseInt(date2, 10);
    const response = await axiosInstance.post(`${SERVER_URL}/api/v1/back/usr?ope=apicalls_list`, {
      prd,
      date1,
      date2
    }, {
      headers: {
        'Authorization': `Bearer ${sessionId}`
      }
    });

    // Add proper null checks
    if (!response || !response.data) {
      return [];
    }
    
    return response.data.apicalls || [];
  } catch (error) {
    console.error('Error al obtener impresiones:', error);
    return []; // Return empty array instead of throwing error
  }
};

/**
 * Envía un mensaje al chatbot de soporte incluyendo los datos del usuario
 * @param {string} message - Mensaje del usuario
 * @param {string} chatId - ID de la conversación (opcional)
 * @returns {Promise<object>} - Respuesta del chatbot
 */
export const sendSupportMessage = async (message, chatId = '', selectedChatbot = null) => {
  try {
    const sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      throw new Error('No hay sesión activa');
    }

    // Obtener datos del usuario desde sessionStorage
    const userData = JSON.parse(sessionStorage.getItem('user'));
    const { name: userName, email: userEmail, organization: userCompany } = userData || {};

    const API_URL = SERVER_URL + "/api/v1/chat";
    const SUPPORT_CHATBOT_CODE = 'utemlugfwn';

    const response = await axios.post(API_URL, {
      clientCode: SUPPORT_CHATBOT_CODE,
      chatId: chatId || '',
      message: message,
      userName: userName || '',
      userEmail: userEmail || '',
      userCompany: selectedChatbot || userCompany || ''
    }, {
      timeout: 60000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${sessionId}`
      },
      validateStatus: function (status) {
        return status >= 200 && status < 500;
      }
    });

    if (!response.data) {
      throw new Error('Respuesta vacía del servidor');
    }

    return response.data;
  } catch (error) {
    manejarError(error);
  }
};